import React, {
  useState,
  useRef,
  useMemo,
  memo,
  useEffect,
  useContext,
  useCallback
} from 'react';
import classNames from 'classnames';
import { Document } from 'react-pdf';
import {
  OverlayScrollbarsComponent,
  OverlayScrollbarsComponentRef
} from 'overlayscrollbars-react';
import { ReactComponent as TextHighlightIcon } from '@assets/icons/ShapesDrawingTools/text_highlight.svg';
import { ReactComponent as CrossSvg } from '@assets/icons/cross.svg';
import { ReactComponent as CheckSvg } from '@assets/icons/check.svg';
import PlaybackPreparingLoader from '@pages/MediaViewer/AssetViewer/PlaybackPreparingLoader';
import { DocumentMetadataDto } from '@api/Api';
import { FabricContext } from '@context/FabricContext';
import FabricCanvas from '@pages/MediaViewer/AssetViewer/Canvas/Canvas';
import 'react-pdf/dist/Page/TextLayer.css';
import { highlightCreator } from '@pages/MediaViewer/AssetViewer/Canvas/Objects/highlightHandler';
import type { AssetViewerZoomOption } from '@context/AssetViewerStateContext/types';
import {
  getInitialPdfPageScale,
  getMobilePdfPageScrollTop,
  getTwoFingerSpan,
  handleSelectionChange,
  isMobilePdfPageRendered,
  pickMostVisiblePage,
  scrollMobileViewportToFabricObjects
} from '@pages/MediaViewer/AssetViewer/PdfCard/utils';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import MobilePdfPage from '@pages/MediaViewer/AssetViewer/PdfCard/components/MobilePdfPage';
import type { PdfCardProps } from '@pages/MediaViewer/AssetViewer/PdfCard/PdfCard';

const maxCanvasSize = 2048;

const minZoom = 0.1;
const maxZoom = 3;

function extractTextFromNode(node: Node): string {
  if (node.nodeName === 'BR') return '\n';
  if (node.nodeType === Node.TEXT_NODE) return node.textContent || '';
  if (node.nodeType === Node.ELEMENT_NODE)
    return Array.from(node.childNodes).map(extractTextFromNode).join('');
  return '';
}

export default memo(function PdfCardMobile({
  onReady,
  asset,
  isAssetReady
}: PdfCardProps) {
  const fabricContext = useContext(FabricContext);
  const {
    selectedCommentId,
    shapesDrawingTools,
    activePageNumber,
    zoomFactor,
    zoomOption,
    turnoverDegree,
    documentHighlight,
    selectComment,
    setActivePageNumber,
    setDocumentHighlight,
    setZoomFactor,
    setZoomOption,
    setCanvasMode,
    setDrawToolsShape,
    setIsCommentFieldFocused
  } = useAssetViewerState();
  const isShapeDrawing = shapesDrawingTools.shape !== 'cursor';
  const isTextHighlightMode = shapesDrawingTools.shape === 'documentHighlight';
  const [selectedTextLength, setSelectedTextLength] = useState(0);
  const documentHighlightRef = useRef(documentHighlight);
  const zoomRef = useRef(zoomFactor);
  zoomRef.current = zoomFactor;

  const pdfPinchGestureRef = useRef({
    isActive: false,
    startSpan: 0,
    baseZoom: 1
  });
  const pendingPinchZoomRef = useRef<number | null>(null);

  const wrapperRef = useRef<OverlayScrollbarsComponentRef<'div'>>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const pageRefsMap = useRef(new Map<number, HTMLDivElement>());
  const skipActivePageScrollSyncRef = useRef(false);
  const activePageFromScrollRef = useRef(false);
  const pendingScrollToPageRef = useRef<number | null>(null);
  const isLoadPdf = useRef<boolean>(false);
  const canvasContainerRef = useRef<HTMLDivElement>(null);

  const textLayersMapRef = useRef<Map<HTMLDivElement, HTMLDivElement>>(
    new Map()
  );

  const fileUrl = useMemo(() => {
    const { proxy } = asset.metadata as DocumentMetadataDto;
    return proxy.url || '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset.id]);

  const mobilePagesReadyRef = useRef(new Set<number>());

  useEffect(() => {
    isLoadPdf.current = false;
    mobilePagesReadyRef.current.clear();
    setIsPageSizeReady(false);
  }, [asset.id]);

  const [pagesCount, setPagesCount] = useState(1);

  const [pageRefsVersion, setPageRefsVersion] = useState(0);
  const [mobilePagesReadyVersion, setMobilePagesReadyVersion] = useState(0);
  const [pdfScrollViewport, setPdfScrollViewport] = useState<Element | null>(
    null
  );
  const [isPageSizeReady, setIsPageSizeReady] = useState(false);
  const [pageSize, setPageSize] = useState<{ width: number; height: number }>({
    width: 1605,
    height: 2272
  });

  const _pageSize = useMemo(() => {
    return {
      width:
        pageSize.width > pageSize.height
          ? pageSize.width * zoomFactor
          : undefined,
      height:
        pageSize.height > pageSize.width
          ? pageSize.height * zoomFactor
          : undefined
    };
  }, [pageSize, zoomFactor]);

  const pagesArray = useMemo(
    () =>
      new Array(pagesCount).fill('').map((_, index) => ({ page: index + 1 })),
    [pagesCount]
  );

  const onRegisterMobilePageRoot = useCallback(
    (page: number, element: HTMLDivElement | null) => {
      const hadElement = pageRefsMap.current.has(page);
      if (element) {
        pageRefsMap.current.set(page, element);
        if (!hadElement) setPageRefsVersion((value) => value + 1);
        return;
      }
      pageRefsMap.current.delete(page);
      if (hadElement) setPageRefsVersion((value) => value + 1);
    },
    []
  );

  const setMobileFabricCanvasVisible = useCallback((visible: boolean) => {
    const canvasContainer = canvasContainerRef.current;
    if (!canvasContainer) return;
    canvasContainer.style.visibility = visible ? 'visible' : 'hidden';
    canvasContainer.style.pointerEvents = visible ? '' : 'none';
  }, []);

  const syncMobileFabricCanvas = useCallback((): boolean => {
    const canvasContainer = canvasContainerRef?.current;
    const content = contentRef?.current;
    if (!canvasContainer || !content || !fabricContext.canvas) return false;

    const pageEl = pageRefsMap.current.get(activePageNumber);
    if (!pageEl || !isMobilePdfPageRendered(pageEl)) return false;

    const targetEl =
      (pageEl.querySelector('.media_viewer_pdf-inner') as HTMLElement | null) ??
      pageEl;

    const rect = targetEl.getBoundingClientRect();
    if (!rect.width || !rect.height) return false;

    content.style.removeProperty('width');
    content.style.removeProperty('height');

    const contentRect = content.getBoundingClientRect();
    const left = rect.left - contentRect.left + rect.width / 2;
    const top = rect.top - contentRect.top + rect.height / 2;

    canvasContainer.style.left = `${left}px`;
    canvasContainer.style.top = `${top}px`;
    canvasContainer.style.width = `${rect.width}px`;
    canvasContainer.style.height = `${rect.height}px`;
    canvasContainer.style.translate = `-${Math.round(
      rect.width / 2
    )}px -${Math.round(rect.height / 2)}px`;

    fabricContext.canvas.setWidth(rect.width);
    fabricContext.canvas.setHeight(rect.height);
    fabricContext.canvas.setViewportTransform([
      zoomFactor * 10,
      0,
      0,
      zoomFactor * 10,
      0,
      0
    ]);
    return true;
  }, [activePageNumber, zoomFactor, fabricContext.canvas, pageRefsVersion]);

  const runMobileCanvasLayout = useCallback(() => {
    if (!fabricContext.canvas) return;

    if (!isPageSizeReady) {
      setMobileFabricCanvasVisible(false);
      return;
    }

    const synced = syncMobileFabricCanvas();
    if (!synced) {
      setMobileFabricCanvasVisible(false);
      return;
    }

    setMobileFabricCanvasVisible(true);
    fabricContext.canvas.requestRenderAll();

    if (!selectedCommentId || selectedCommentId === 'new') return;

    const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
    const canvasEl = fabricContext.canvas.getElement();
    if (!viewport || !canvasEl) return;

    scrollMobileViewportToFabricObjects(viewport, canvasEl, () => {
      const objects = fabricContext.canvas
        ?.getObjects()
        .filter((item) => item.type !== 'highlight' && item.visible !== false);
      if (!objects?.length) return null;

      const canvasRect = canvasEl.getBoundingClientRect();
      let minTop = Number.POSITIVE_INFINITY;
      let maxBottom = Number.NEGATIVE_INFINITY;

      objects.forEach((obj) => {
        const bounds = obj.getBoundingRect(true);
        const top = canvasRect.top + bounds.top;
        const bottom = top + bounds.height;
        minTop = Math.min(minTop, top);
        maxBottom = Math.max(maxBottom, bottom);
      });

      if (!Number.isFinite(minTop)) return null;
      return { top: minTop, bottom: maxBottom };
    });

    syncMobileFabricCanvas();
    fabricContext.canvas.requestRenderAll();
  }, [
    isPageSizeReady,
    fabricContext.canvas,
    syncMobileFabricCanvas,
    setMobileFabricCanvasVisible,
    selectedCommentId
  ]);

  const onMobilePageLoadSuccess = useCallback((page: number) => {
    if (mobilePagesReadyRef.current.has(page)) return;
    mobilePagesReadyRef.current.add(page);
    setMobilePagesReadyVersion((value) => value + 1);
  }, []);

  const scrollMobilePdfToPage = useCallback((pageNumber: number) => {
    const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
    const pageEl = pageRefsMap.current.get(pageNumber);
    if (!viewport || !pageEl) {
      pendingScrollToPageRef.current = pageNumber;
      return false;
    }

    pendingScrollToPageRef.current = null;
    skipActivePageScrollSyncRef.current = true;
    viewport.scrollTo({
      top: getMobilePdfPageScrollTop(viewport, pageEl),
      behavior: 'instant'
    });
    window.setTimeout(() => {
      skipActivePageScrollSyncRef.current = false;
    }, 500);
    return true;
  }, []);

  const updateActivePageFromScroll = useCallback(() => {
    if (skipActivePageScrollSyncRef.current) return;
    if (selectedCommentId) return;

    const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
    if (!viewport) return;

    const nextPage = pickMostVisiblePage(
      pagesArray,
      pageRefsMap.current,
      viewport
    );
    if (!nextPage || nextPage === activePageNumber) return;

    activePageFromScrollRef.current = true;
    setActivePageNumber(nextPage);
    if (
      shapesDrawingTools.shape !== 'documentHighlight' &&
      !selectedCommentId
    ) {
      selectComment(null);
      setDocumentHighlight(null);
      setCanvasMode('view');
    }
  }, [
    pagesArray,
    activePageNumber,
    shapesDrawingTools.shape,
    selectedCommentId,
    setActivePageNumber,
    selectComment,
    setDocumentHighlight,
    setCanvasMode
  ]);

  const onClearHighlightObjects = useCallback(() => {
    const { canvas } = fabricContext;
    if (!canvas) return;
    const getHighlightObjects = canvas
      .getObjects()
      .filter((item) => item.type === 'highlight');
    getHighlightObjects.forEach((obj) => canvas.remove(obj));
    canvas.fire('object:history:remove');
  }, [fabricContext.canvas]);

  const renderTextHighlights = useCallback(
    (rects: any) => {
      if (!fabricContext.canvas) return;
      onClearHighlightObjects();
      highlightCreator({
        canvas: fabricContext.canvas,
        documentHighlightRects: rects,
        viewerActions: {
          selectComment,
          setCanvasMode,
          setDocumentHighlight
        }
      });
    },
    [fabricContext.canvas, onClearHighlightObjects]
  );

  useEffect(() => {
    if (!selectedCommentId) return;
    if (
      documentHighlightRef.current?.rects?.length &&
      !documentHighlight.rects?.length
    ) {
      onClearHighlightObjects();
    }
  }, [selectedCommentId, documentHighlight.rects, onClearHighlightObjects]);

  useEffect(() => {
    if (!isShapeDrawing) return;
    if (shapesDrawingTools.shape !== 'documentHighlight') return;
    const pageCanvas = pageCanvasRef.current;
    let prevRange: Range | null = null;

    const onSelectionchange = () => {
      if (pdfPinchGestureRef.current.isActive) return;

      textLayersMapRef.current.clear();
      pageRefsMap.current.forEach((pageRoot) => {
        const textLayerDiv = pageRoot?.querySelector('.textLayer');
        const endDiv = pageRoot?.querySelector('.endOfContent');
        if (!textLayerDiv || !endDiv) return;
        textLayersMapRef.current.set(
          textLayerDiv as HTMLDivElement,
          endDiv as HTMLDivElement
        );
      });

      const range = handleSelectionChange(textLayersMapRef.current, prevRange);
      prevRange = range;

      if (!range) {
        const stagedHighlight = documentHighlightRef.current;
        if (!stagedHighlight?.rects?.length) {
          onClearHighlightObjects();
          setSelectedTextLength(0);
          return;
        }
        renderTextHighlights(stagedHighlight.rects);
        return;
      }

      const pageRoot = pageRefsMap.current.get(activePageNumber);
      const pageCoordsElement =
        pageRoot?.querySelector('.media_viewer_pdf-inner') ?? pageCanvas;

      if (!pageCoordsElement) {
        onClearHighlightObjects();
        setSelectedTextLength(0);
        return;
      }

      const rects = Array.from(range.getClientRects()).filter(
        (r) => r.width > 0 && r.height > 0
      );
      if (!rects.length) {
        onClearHighlightObjects();
        setSelectedTextLength(0);
        return;
      }
      const canvasRect = pageCoordsElement.getBoundingClientRect();
      const selectedCoords = rects
        .map((rect) => ({
          left: ((rect.left - canvasRect.left) / canvasRect.width) * 100,
          top: ((rect.top - canvasRect.top) / canvasRect.height) * 100,
          width: (rect.width / canvasRect.width) * 100,
          height: (rect.height / canvasRect.height) * 100
        }))
        .filter((r) => r.width > 0 && r.height > 0);

      if (!selectedCoords.length) {
        onClearHighlightObjects();
        setSelectedTextLength(0);
        return;
      }

      const fragment = range.cloneContents();
      documentHighlightRef.current = {
        text: Array.from(fragment.childNodes)
          .map(extractTextFromNode)
          .join('')
          .trim(),
        rects: selectedCoords
      };
      setSelectedTextLength(documentHighlightRef.current.text?.length || 0);
      renderTextHighlights(selectedCoords);
    };

    document.addEventListener('selectionchange', onSelectionchange);
    return () => {
      prevRange = null;
      textLayersMapRef.current.clear();
      document.removeEventListener('selectionchange', onSelectionchange);
    };
  }, [
    selectedCommentId,
    shapesDrawingTools,
    isShapeDrawing,
    fabricContext.canvas,
    activePageNumber,
    setDocumentHighlight,
    onClearHighlightObjects,
    renderTextHighlights
  ]);

  useEffect(() => {
    const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
    if (!viewport) return;

    updateActivePageFromScroll();

    viewport.addEventListener('scroll', updateActivePageFromScroll, {
      passive: true
    });
    window.addEventListener('resize', updateActivePageFromScroll);

    return () => {
      viewport.removeEventListener('scroll', updateActivePageFromScroll);
      window.removeEventListener('resize', updateActivePageFromScroll);
    };
  }, [updateActivePageFromScroll, pageRefsVersion]);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      const viewport =
        wrapperRef.current?.osInstance()?.elements()?.viewport ?? null;
      setPdfScrollViewport(viewport);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [isAssetReady, pagesCount]);

  useEffect(() => {
    if (!pdfScrollViewport) return undefined;

    const pinch = pdfPinchGestureRef.current;

    const applyPinchPreview = (nextZoom: number) => {
      const content = contentRef.current;
      if (!content || pinch.baseZoom <= 0) return;
      const scale = nextZoom / pinch.baseZoom;
      content.style.transformOrigin = 'center top';
      content.style.transform = `scale(${scale})`;
    };

    const clearPinchPreview = () => {
      const content = contentRef.current;
      if (!content) return;
      content.style.transform = '';
      content.style.transformOrigin = '';
    };

    const commitPinchZoom = () => {
      const nextZoom = pendingPinchZoomRef.current;
      pendingPinchZoomRef.current = null;
      clearPinchPreview();
      if (nextZoom == null || nextZoom === zoomRef.current) return;
      setZoomFactor(nextZoom);
    };

    const onTouchStart = (e: Event) => {
      const touchEvent = e as TouchEvent;
      if (touchEvent.touches.length < 2) return;
      const span = getTwoFingerSpan(touchEvent.touches);
      if (span < 10) return;
      if (!pinch.isActive) {
        pinch.isActive = true;
        pinch.startSpan = span;
        pinch.baseZoom = zoomRef.current;
        pendingPinchZoomRef.current = null;
        clearPinchPreview();
      }
    };

    const onTouchMove = (e: Event) => {
      const touchEvent = e as TouchEvent;
      if (!pinch.isActive || touchEvent.touches.length < 2) return;
      const span = getTwoFingerSpan(touchEvent.touches);
      if (pinch.startSpan < 10) return;
      const nextZoom = Math.min(
        maxZoom,
        Math.max(minZoom, pinch.baseZoom * (span / pinch.startSpan))
      );
      touchEvent.preventDefault();
      pendingPinchZoomRef.current = nextZoom;
      applyPinchPreview(nextZoom);
    };

    const onTouchEnd = (e: Event) => {
      const touchEvent = e as TouchEvent;
      if (touchEvent.touches.length < 2 && pinch.isActive) {
        pinch.isActive = false;
        commitPinchZoom();
      }
    };

    const listenerCapturePassive = { capture: true, passive: true } as const;
    const listenerCaptureNotPassive = {
      capture: true,
      passive: false
    } as const;

    pdfScrollViewport.addEventListener(
      'touchstart',
      onTouchStart,
      listenerCapturePassive
    );
    pdfScrollViewport.addEventListener(
      'touchmove',
      onTouchMove,
      listenerCaptureNotPassive
    );
    pdfScrollViewport.addEventListener(
      'touchend',
      onTouchEnd,
      listenerCapturePassive
    );
    pdfScrollViewport.addEventListener(
      'touchcancel',
      onTouchEnd,
      listenerCapturePassive
    );

    return () => {
      pinch.isActive = false;
      pendingPinchZoomRef.current = null;
      clearPinchPreview();
      pdfScrollViewport.removeEventListener(
        'touchstart',
        onTouchStart,
        listenerCapturePassive
      );
      pdfScrollViewport.removeEventListener(
        'touchmove',
        onTouchMove,
        listenerCaptureNotPassive
      );
      pdfScrollViewport.removeEventListener(
        'touchend',
        onTouchEnd,
        listenerCapturePassive
      );
      pdfScrollViewport.removeEventListener(
        'touchcancel',
        onTouchEnd,
        listenerCapturePassive
      );
    };
  }, [pdfScrollViewport, setZoomFactor]);

  useEffect(() => {
    if (activePageFromScrollRef.current) {
      activePageFromScrollRef.current = false;
      return;
    }

    if (scrollMobilePdfToPage(activePageNumber)) {
      requestAnimationFrame(() => {
        runMobileCanvasLayout();
      });
    }
  }, [activePageNumber, scrollMobilePdfToPage, runMobileCanvasLayout]);

  useEffect(() => {
    if (pendingScrollToPageRef.current == null) return;
    if (scrollMobilePdfToPage(pendingScrollToPageRef.current)) {
      requestAnimationFrame(() => {
        runMobileCanvasLayout();
      });
    }
  }, [pageRefsVersion, scrollMobilePdfToPage, runMobileCanvasLayout]);

  const getZoomFactorFromOption = useCallback(
    (option: NonNullable<AssetViewerZoomOption>) => {
      if (option === 'actual-size') return 1;
      const container = wrapperRef?.current?.getElement();
      if (!container || !isPageSizeReady) return 1;
      const width =
        Math.abs(pageSize.width * Math.cos(turnoverDegree * (Math.PI / 180))) +
        Math.abs(pageSize.height * Math.sin(turnoverDegree * (Math.PI / 180)));
      const height =
        Math.abs(pageSize.width * Math.sin(turnoverDegree * (Math.PI / 180))) +
        Math.abs(pageSize.height * Math.cos(turnoverDegree * (Math.PI / 180)));
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;
      const scaleWidth = Math.floor(containerWidth / (width / 100)) / 100;
      const scaleHeight = Math.floor(containerHeight / (height / 100)) / 100;
      if (option === 'auto' || option === 'fit-width') {
        return scaleWidth;
      }
      if (option === 'fit-height') {
        return scaleHeight;
      }
      return 1;
    },
    [pageSize, turnoverDegree, isPageSizeReady]
  );

  useEffect(() => {
    setZoomOption('fit-width');
  }, [setZoomOption]);

  useEffect(() => {
    if (!zoomOption || !isPageSizeReady) return undefined;

    const applyFitZoom = () => {
      if (pdfPinchGestureRef.current.isActive) return;
      const newZoomFactor = getZoomFactorFromOption(zoomOption);
      setZoomFactor(newZoomFactor, zoomOption);
    };

    applyFitZoom();
    window.addEventListener('resize', applyFitZoom);
    return () => window.removeEventListener('resize', applyFitZoom);
  }, [zoomOption, isPageSizeReady, getZoomFactorFromOption, setZoomFactor]);

  useEffect(() => {
    runMobileCanvasLayout();

    const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
    viewport?.addEventListener('scroll', runMobileCanvasLayout, {
      passive: true
    });
    window.addEventListener('resize', runMobileCanvasLayout);

    return () => {
      viewport?.removeEventListener('scroll', runMobileCanvasLayout);
      window.removeEventListener('resize', runMobileCanvasLayout);
    };
  }, [runMobileCanvasLayout]);

  useEffect(() => {
    if (!fabricContext.canvas) return undefined;

    const onAnnotationsLoaded = () => {
      requestAnimationFrame(runMobileCanvasLayout);
    };

    fabricContext.canvas.on('pdf:annotations-loaded', onAnnotationsLoaded);
    return () => {
      fabricContext.canvas?.off('pdf:annotations-loaded', onAnnotationsLoaded);
    };
  }, [fabricContext.canvas, runMobileCanvasLayout]);

  useEffect(() => {
    requestAnimationFrame(runMobileCanvasLayout);
  }, [
    selectedCommentId,
    activePageNumber,
    pageRefsVersion,
    mobilePagesReadyVersion,
    zoomFactor,
    runMobileCanvasLayout
  ]);

  useEffect(() => {
    if (shapesDrawingTools.shape !== 'documentHighlight') return;
    if (!documentHighlightRef.current?.rects?.length) return;
    renderTextHighlights(documentHighlightRef.current.rects);
  }, [zoomFactor, shapesDrawingTools.shape, renderTextHighlights]);

  const onCancelTextHighlight = useCallback(() => {
    document.getSelection()?.removeAllRanges();
    setSelectedTextLength(0);
    onClearHighlightObjects();
    setDocumentHighlight(null);
    setDrawToolsShape('cursor');
  }, [onClearHighlightObjects, setDocumentHighlight, setDrawToolsShape]);

  const onConfirmTextHighlight = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const highlight = documentHighlightRef.current;
      if (!highlight?.rects?.length) return;

      setDocumentHighlight({
        text: highlight.text ?? '',
        rects: highlight.rects
      });
      setSelectedTextLength(0);
      setDrawToolsShape('cursor');
      setIsCommentFieldFocused(true);
      document.getSelection()?.removeAllRanges();
    },
    [setDocumentHighlight, setDrawToolsShape, setIsCommentFieldFocused]
  );

  const pageCanvasRef = useRef<HTMLCanvasElement>(null);
  const onLoadSuccess = useCallback(
    (pdfPage: { originalWidth: number; originalHeight: number }) => {
      if (isLoadPdf.current) return;
      isLoadPdf.current = true;
      const { originalWidth, originalHeight } = pdfPage;
      setPageSize(
        getInitialPdfPageScale(
          originalWidth,
          originalHeight,
          maxCanvasSize,
          maxZoom,
          true
        )
      );
      setIsPageSizeReady(true);
    },
    []
  );

  return (
    <Document
      className={classNames('media_viewer_pdf', {
        'b-hide-shapes-drawing':
          shapesDrawingTools?.shape === 'documentHighlight' && isShapeDrawing
      })}
      file={fileUrl}
      onLoadSuccess={(pdf) => {
        setPagesCount(pdf.numPages);
        onReady();
      }}
      loading={<PlaybackPreparingLoader />}
    >
      <div className="media_viewer_pdf-page-indicator">
        {activePageNumber} of {pagesCount}
      </div>
      <OverlayScrollbarsComponent
        className="media_viewer_pdf-wrapper"
        ref={wrapperRef}
      >
        <div
          className={classNames(
            'media_viewer_pdf-content',
            'media_viewer_pdf-content-mobile'
          )}
          ref={contentRef}
        >
          <div className="media_viewer_pdf-mobile-pages">
            {pagesArray.map(({ page }) => (
              <MobilePdfPage
                key={page}
                pageNumber={page}
                pageWidth={_pageSize.width}
                pageHeight={_pageSize.height}
                turnoverDegree={turnoverDegree}
                isActive={page === activePageNumber}
                isFirstPage={page === 1}
                pageCanvasRef={pageCanvasRef}
                onRegisterPageRoot={onRegisterMobilePageRoot}
                onFirstPageLoadSuccess={onLoadSuccess}
                onPageLoadSuccess={onMobilePageLoadSuccess}
                intersectionRoot={pdfScrollViewport}
              />
            ))}
          </div>
          <div ref={canvasContainerRef} className="media_viewer_pdf-canvas">
            <FabricCanvas />
          </div>
        </div>
      </OverlayScrollbarsComponent>
      {!isAssetReady && <PlaybackPreparingLoader />}
      {isTextHighlightMode && (
        <div className="media_viewer_pdf-text-highlight-mobile">
          <div className="media_viewer_pdf-text-highlight-mobile-icon">
            <TextHighlightIcon />
          </div>
          <div className="media_viewer_pdf-text-highlight-mobile-content">
            {selectedTextLength
              ? `${selectedTextLength} characters selected`
              : 'Select text to attach'}
          </div>
          <div className="media_viewer_pdf-text-highlight-mobile-btn-container">
            <div
              className="media_viewer_pdf-text-highlight-mobile-btn"
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onCancelTextHighlight();
              }}
            >
              <CrossSvg width={20} height={20} />
            </div>
            {!!selectedTextLength && (
              <div
                className="media_viewer_pdf-text-highlight-mobile-btn"
                onPointerDown={onConfirmTextHighlight}
              >
                <CheckSvg width={14} height={14} />
              </div>
            )}
          </div>
        </div>
      )}
    </Document>
  );
});
